import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import store from "../Common/Store";

function SlideBar(props) {
  const [stateAngles, setStateAngles] = useState(false);
  const [stateLabels, setStateLabels] = useState(false);
  const [helpTopic, setHelpTopic] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (props.access) {
      setStateAngles(props.access.angles);
      setStateLabels(props.access.labels);
    }
  }, [props.access.angles, props.access.labels, props.access]);

  const toggleHelp = (topic) => {
    setHelpTopic((current) => (current === topic ? null : topic));
  };

  const ensureLabelsVisible = () => {
    if (!props.access.labels && !props.access.angles) {
      props.access.labels = true;
      localStorage.setItem("hm_labels", true);
      setStateLabels(true);
      props.access.showLabels(props.canva);
    }
  };

  const setCanvasZoom = (zoomIn) => {
    const canva = props.canva;
    const poly = props.poly || canva?.lastPolygon;
    const stage = canva?.stage;

    if (!canva || !poly || !stage) {
      return;
    }

    ensureLabelsVisible();

    const CanvasClass = canva.constructor;
    const PolygonClass = poly.constructor;
    const currentScale = CanvasClass.scale;
    const nextScale = Math.max(
      CanvasClass.baseScale * 0.35,
      Math.min(
        CanvasClass.baseScale * 1.45,
        Math.round(currentScale * (zoomIn ? 1.1 : 0.9) * 100) / 100
      )
    );

    if (nextScale === currentScale) {
      return;
    }

    const points = poly.getPointsCoords();
    const xValues = points.filter((_, index) => index % 2 === 0);
    const yValues = points.filter((_, index) => index % 2 === 1);
    const anchor = {
      x: (Math.min(...xValues) + Math.max(...xValues)) / 2,
      y: (Math.min(...yValues) + Math.max(...yValues)) / 2,
    };
    const scaleFactor = nextScale / currentScale;

    poly.points.forEach((point) => {
      point.x = anchor.x + (point.x - anchor.x) * scaleFactor;
      point.y = anchor.y + (point.y - anchor.y) * scaleFactor;
    });

    CanvasClass.scale = nextScale;
    PolygonClass.scale = nextScale;
    poly.measurementScale = nextScale;

    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    stage.find(".poly").forEach((shape) => {
      if (shape.getAttr("polygonObj") === poly) {
        shape.points(poly.getPointsCoords());
      }
    });

    const desktopCanvasOffset = window.matchMedia("(min-width: 1024px)")
      .matches
      ? 70
      : 0;

    CanvasClass.polyDragFunc(canva, poly, props.access, false)(
      { x: poly.x, y: poly.y },
      {
        clientX: canva.width / 2 + desktopCanvasOffset,
        clientY: canva.height / 2,
      }
    );

    if (props.access.angles) {
      props.access.showAnglesButton(canva);
    } else {
      props.access.hideAnglesButton(canva);
    }

    if (props.func) {
      props.func(nextScale);
    }

    stage.batchDraw();
  };

  const resetPlan = () => {
    if (props.canva) {
      props.canva.deleteDraw(true);
    }
    store.empty();
    if (props.eraseFunc) {
      props.eraseFunc();
    }
  };

  return (
    <>
      <div
        className={`slideBar${
          props.hiddenOnMobile ? " slideBar--thermostat" : ""
        }${props.coldSpotStep ? " slideBar--cold-spots" : ""}`}
      >
        <div className="slideBar-inner">
          <div className="slideBar-controls">
            <div className="slider_wrap">
              <div className="slideBar-control-title">
                <span className="slider_text">{t("Angles")}</span>
                <button
                  type="button"
                  className="slideBar-help-btn slideBar-control-help"
                  onClick={() => toggleHelp("angles")}
                  aria-label={t("How to use angles mode")}
                >
                  ?
                </button>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={!!stateAngles}
                  onChange={(e) => {
                    props.access.angles = e.target.checked;
                    setStateAngles(props.access.angles);
                    if (e.target.checked) {
                      props.access.labels = false;
                      localStorage.setItem("hm_labels", false);
                      setStateLabels(false);
                      props.access.hideLabels(props.canva);
                      props.access.showAnglesButton(props.canva);
                    } else {
                      props.access.hideAnglesButton(props.canva);
                    }
                  }}
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="slider_wrap">
              <div className="slideBar-control-title">
                <span className="slider_text">{t("Labels")}</span>
                <button
                  type="button"
                  className="slideBar-help-btn slideBar-control-help"
                  onClick={() => toggleHelp("labels")}
                  aria-label={t("About dimension labels")}
                >
                  ?
                </button>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={!!stateLabels}
                  onChange={(e) => {
                    if (e.target.checked && props.access.angles) {
                      props.access.angles = false;
                      setStateAngles(false);
                      props.access.hideAnglesButton(props.canva);
                    }
                    props.access.labels = e.target.checked;
                    localStorage.setItem("hm_labels", props.access.labels);
                    setStateLabels(props.access.labels);
                    if (e.target.checked) {
                      props.access.showLabels(props.canva);
                    } else {
                      props.access.hideLabels(props.canva);
                    }
                  }}
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div
              className={`slider_wrap ${
                props.scaleDisabled ? "slider_wrap--scale-disabled" : ""
              }`}
            >
              <div className="slideBar-control-title">
                <span className="slider_text">{t("Scale")}</span>
                <button
                  type="button"
                  className="slideBar-help-btn slideBar-control-help"
                  onClick={() => toggleHelp("scale")}
                  aria-label={t("About drawing scale")}
                >
                  ?
                </button>
              </div>
              <label className="switch">
                <span
                  className={`slider_empty round ${
                    props.scaleDisabled ? "slider_empty--disabled" : ""
                  }`}
                  aria-disabled={!!props.scaleDisabled}
                  title={
                    props.scaleDisabled
                      ? t("Scale is available while setting the room size")
                      : undefined
                  }
                >
                  <button
                    type="button"
                    className="plus_minus plus_minus-minus"
                    aria-label={t("Zoom out and show more space")}
                    disabled={!!props.scaleDisabled}
                    onClick={() => setCanvasZoom(false)}
                  >
                    -
                  </button>

                  <button
                    type="button"
                    className="plus_minus plus_minus-plus"
                    aria-label={t("Zoom in")}
                    disabled={!!props.scaleDisabled}
                    onClick={() => setCanvasZoom(true)}
                  >
                    +
                  </button>
                </span>
              </label>
            </div>

            {props.erase && (
              <div className="slider_wrap slider_wrap--reset-plan">
                <div className="slideBar-control-title">
                  <span className="slider_text">{t("Reset plan")}</span>
                  <button
                    type="button"
                    className="slideBar-help-btn slideBar-control-help"
                    onClick={() => toggleHelp("reset")}
                    aria-label={t("How to reset plan")}
                  >
                    ?
                  </button>
                </div>
                <button
                  type="button"
                  className="reset-plan-button"
                  onClick={resetPlan}
                  aria-label={t("Reset plan")}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.75 10h-2.1A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h8V3l-3.35 3.35Z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {helpTopic && (
        <div className="slideBar-help-bubble">
          <div className="slideBar-help-title">
            {helpTopic === "angles" && t("Using Angles mode")}
            {helpTopic === "labels" && t("Labels")}
            {helpTopic === "scale" && t("Scale")}
            {helpTopic === "reset" && t("Reset plan")}
          </div>
          <div className="slideBar-help-body">
            {helpTopic === "angles" && (
              <p>{t("Angles help text")}</p>
            )}
            {helpTopic === "labels" && (
              <p>{t("Labels help text")}</p>
            )}
            {helpTopic === "scale" && (
              <p>
                {props.scaleDisabled
                  ? t("Scale disabled help text")
                  : t("Scale help text")}
              </p>
            )}
            {helpTopic === "reset" && (
              <p>{t("Reset plan help text")}</p>
            )}
          </div>
          <button
            type="button"
            className="slideBar-help-close"
            onClick={() => setHelpTopic(null)}
          >
            {t("Close")}
          </button>
        </div>
      )}
    </>
  );
}

export default SlideBar;
